import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mic, Square, X, Loader2, Check, AlertTriangle, Keyboard } from "lucide-react";
import Modal from "@/components/Modal";
import VoiceEditModal from "@/components/VoiceEditModal";
import { Btn, Input, Textarea, Select, Field, Badge } from "@/components/common";
import { api, apiError } from "@/lib/api";

const INTENT_LABEL = {
  add_to_group: "Add to group", remove_from_group: "Remove from group", open_group: "Open group",
  change_importance: "Change importance", add_note: "Add note", archive_contact: "Archive contact",
  delete_contact: "Delete contact", add_spider: "Add to Spider network", remove_spider: "Remove from Spider network",
  edit_field: "Edit contact", email_contact: "Email contact", update_contact: "Update contact by voice", unknown: "Unrecognized command",
};

export default function VoiceCommandModal({ workspaceId, onClose }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("idle"); // idle | recording | processing | confirm | error
  const [transcript, setTranscript] = useState("");
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState("");
  const [contactId, setContactId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [originId, setOriginId] = useState("");
  const [busy, setBusy] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // {id, name, transcript} -> opens VoiceEditModal
  const mediaRef = useRef(null); const chunksRef = useRef([]); const streamRef = useRef(null); const seconds = useRef(0); const timerRef = useRef(null);

  const stopAll = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop(); } catch {}
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  const start = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      let mime = ""; for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) { if (MediaRecorder.isTypeSupported(m)) { mime = m; break; } }
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream); mediaRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        await transcribe(blob);
      };
      mr.start(); seconds.current = 0; setPhase("recording");
      timerRef.current = setInterval(() => { seconds.current += 1; if (seconds.current >= 30) stop(); }, 1000);
    } catch { setError("Microphone unavailable. Type your command instead."); setPhase("error"); }
  };
  const stop = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { mediaRef.current?.stop(); } catch {}
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  };
  const transcribe = async (blob) => {
    setPhase("processing");
    try {
      const fd = new FormData(); fd.append("file", blob, "cmd.webm");
      const { data } = await api.post(`/voice/transcribe?workspace_id=${workspaceId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const txt = (data.transcript || "").trim();
      setTranscript(txt);
      if (!txt) { setError("No speech detected. Try again or type the command."); setPhase("error"); return; }
      await parse(txt);
    } catch (err) { setError(apiError(err.response?.data?.detail) || "Transcription failed."); setPhase("error"); }
  };
  const parse = async (txt) => {
    setPhase("processing");
    try {
      const { data } = await api.post(`/voice/command/parse?workspace_id=${workspaceId}`, { transcript: txt });
      setParsed(data);
      setContactId(data.contact_candidates?.length === 1 ? data.contact_candidates[0].id : "");
      setGroupId(data.group_candidates?.length === 1 ? data.group_candidates[0].id : "");
      setOriginId(data.origin_candidates?.length === 1 ? data.origin_candidates[0].id : "");
      setPhase("confirm");
    } catch (err) { setError(apiError(err.response?.data?.detail) || "Could not understand the command."); setPhase("error"); }
  };

  const needContact = ["add_to_group", "remove_from_group", "change_importance", "add_note", "archive_contact", "delete_contact", "add_spider", "remove_spider", "edit_field", "email_contact", "update_contact"];
  const needGroup = ["add_to_group", "remove_from_group", "open_group"];
  const needOrigin = ["add_spider", "remove_spider"];
  const intent = parsed?.intent || "unknown";
  const p = parsed?.parsed || {};

  const execute = async () => {
    if (needContact.includes(intent) && !contactId) return toast.error("Select which contact you mean");
    if (needGroup.includes(intent) && !groupId) return toast.error("Select which group you mean");
    if (needOrigin.includes(intent) && !originId) return toast.error("Select which Spider network you mean");
    setBusy(true);
    try {
      if (intent === "update_contact") {
        const c = parsed.contact_candidates.find((x) => x.id === contactId);
        setBusy(false);
        setEditTarget({ id: contactId, name: c?.name || p.contact_name, transcript });
        return;
      }
      if (intent === "open_group") { onClose(); navigate(`/app/groups?group=${groupId}`); return; }
      if (intent === "add_to_group") { await api.post(`/groups/${groupId}/members?workspace_id=${workspaceId}`, { entity_ids: [contactId], note: "" }); toast.success("Added to group"); }
      else if (intent === "remove_from_group") { await api.delete(`/groups/${groupId}/members/${contactId}?workspace_id=${workspaceId}`); toast.success("Removed from group"); }
      else if (intent === "change_importance") { await api.post(`/entities/${contactId}/importance?workspace_id=${workspaceId}`, { level: p.importance || "medium", reason: "Set by voice command" }); toast.success("Importance updated"); }
      else if (intent === "add_note") { await api.post(`/notes?workspace_id=${workspaceId}`, { body: p.note || transcript, entity_ids: [contactId], category: "general", source_type: "user_interpretation" }); toast.success("Note added"); }
      else if (intent === "archive_contact") { await api.delete(`/entities/${contactId}?workspace_id=${workspaceId}`); toast.success("Contact archived"); }
      else if (intent === "add_spider") { await api.post(`/memberships?workspace_id=${workspaceId}`, { origin_id: originId, entity_id: contactId, review_status: "included" }); toast.success("Added to Spider network"); }
      else if (intent === "remove_spider") {
        const full = await api.get(`/entities/${contactId}/full?workspace_id=${workspaceId}`);
        const m = (full.data.memberships || []).find((x) => x.origin_id === originId);
        if (m) { await api.delete(`/memberships/${m.id}?workspace_id=${workspaceId}`); toast.success("Removed from Spider network"); } else toast.error("Contact is not in that network");
      }
      else if (intent === "delete_contact") { onClose(); navigate(`/app/entities/${contactId}`); toast.message("Open the contact and use the owner-only Delete (with confirmation)."); return; }
      else if (intent === "edit_field") { onClose(); navigate(`/app/entities/${contactId}`); toast.message("Opened the contact — edit the field on the Identity tab."); return; }
      else if (intent === "email_contact") { onClose(); navigate(`/app/entities/${contactId}`); toast.message("Email sending is coming soon — opened the contact."); return; }
      onClose();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Command failed"); setBusy(false); }
  };

  return (
    <>
    {editTarget && (
      <VoiceEditModal workspaceId={workspaceId} entityId={editTarget.id} entityName={editTarget.name}
        initialTranscript={editTarget.transcript} onClose={() => { setEditTarget(null); onClose(); }} onSaved={() => {}} />
    )}
    {!editTarget && (
    <Modal open onClose={() => { stopAll(); onClose(); }} title="Voice command" width={560} testid="voice-command-modal">
      {phase === "idle" && (
        <div className="text-center py-6">
          <button onClick={start} className="mx-auto flex items-center justify-center transition-transform duration-200 hover:scale-105" style={{ width: 88, height: 88, borderRadius: "50%", background: "var(--si-teal)", border: "2px solid var(--si-teal-light)" }} data-testid="voice-cmd-record">
            <Mic size={34} color="#fff" strokeWidth={1.5} />
          </button>
          <div className="mt-4 text-sm" style={{ color: "var(--si-text)" }}>Speak a command — e.g. “Add Margaret Hale to Broker-Dealer Targets”, “Show my Wellsboro Contacts”, “Set John Smith importance to high”.</div>
          <div className="mt-2 text-xs" style={{ color: "var(--si-text-2)" }}>Nothing happens until you review and confirm. Ambiguous names need an explicit pick.</div>
          <div className="mt-4"><Btn variant="ghost" onClick={() => { setTranscript(""); setPhase("confirm"); setParsed({ intent: "unknown", parsed: {} }); }} data-testid="voice-cmd-type"><Keyboard size={14} /> Type instead</Btn></div>
        </div>
      )}
      {phase === "recording" && (
        <div className="text-center py-10">
          <div className="mx-auto flex items-center justify-center blink" style={{ width: 88, height: 88, borderRadius: "50%", background: "rgba(239,68,68,0.15)", border: "2px solid var(--si-danger)" }}><Mic size={34} color="#EF4444" /></div>
          <div className="si-label mt-4">listening… (auto-stops at 30s)</div>
          <div className="mt-5"><Btn variant="primary" onClick={stop} data-testid="voice-cmd-stop"><Square size={15} /> Stop</Btn></div>
        </div>
      )}
      {phase === "processing" && <div className="text-center py-16"><Loader2 size={28} className="mx-auto animate-spin" style={{ color: "var(--si-teal-light)" }} /><div className="si-label mt-3">Understanding command…</div></div>}
      {phase === "error" && (
        <div className="py-6">
          <div className="flex items-start gap-2 p-3 mb-4" style={{ border: "1px solid var(--si-danger)", borderRadius: 2 }}><AlertTriangle size={16} style={{ color: "var(--si-danger)" }} /><div className="text-sm" style={{ color: "var(--si-text)" }}>{error}</div></div>
          <div className="flex justify-center gap-2"><Btn variant="secondary" onClick={() => setPhase("idle")}>Try again</Btn></div>
        </div>
      )}
      {phase === "confirm" && (
        <div className="space-y-4">
          <Field label="Command (editable)"><Textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} data-testid="voice-cmd-transcript" placeholder="Type a command…" /></Field>
          {(!parsed || intent === "unknown") && (
            <div className="flex items-center justify-between">
              <span className="si-label" style={{ color: "var(--si-warning)" }}>{parsed ? "Command not recognized — rephrase and re-interpret." : ""}</span>
              <Btn variant="secondary" onClick={() => parse(transcript)} disabled={!transcript.trim()} data-testid="voice-cmd-reparse">Interpret</Btn>
            </div>
          )}
          {parsed && intent !== "unknown" && (
            <>
              <div className="p-3" style={{ background: "var(--si-bg)", border: "1px solid var(--si-border)", borderRadius: 2 }}>
                <div className="flex items-center gap-2 flex-wrap"><Badge value="confirmed">{INTENT_LABEL[intent]}</Badge>
                  {p.contact_name && <span className="text-sm" style={{ color: "var(--si-text)" }}>{p.contact_name}</span>}
                  {p.group_name && <span className="text-sm" style={{ color: "var(--si-text-2)" }}>→ {p.group_name}</span>}
                  {p.importance && <span className="text-sm" style={{ color: "var(--si-text-2)" }}>→ {p.importance}</span>}
                </div>
                <div className="si-label mt-1">Review the exact change below, then confirm. Nothing is saved until you press Confirm.</div>
              </div>
              {needContact.includes(intent) && (
                <Field label="Which contact?">
                  {parsed.contact_candidates?.length ? (
                    <Select value={contactId} onChange={(e) => setContactId(e.target.value)} data-testid="voice-cmd-contact"><option value="">— select —</option>{parsed.contact_candidates.map((c) => <option key={c.id} value={c.id}>{`${c.name} (${c.type})`}</option>)}</Select>
                  ) : <div className="si-label" style={{ color: "var(--si-warning)" }}>No contact matches “{p.contact_name}”. Create it first, then retry.</div>}
                </Field>
              )}
              {needGroup.includes(intent) && (
                <Field label="Which group?">
                  {parsed.group_candidates?.length ? (
                    <Select value={groupId} onChange={(e) => setGroupId(e.target.value)} data-testid="voice-cmd-group"><option value="">— select —</option>{parsed.group_candidates.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</Select>
                  ) : <div className="si-label" style={{ color: "var(--si-warning)" }}>No group matches “{p.group_name}”. Create it first — commands never auto-create groups.</div>}
                </Field>
              )}
              {needOrigin.includes(intent) && (
                <Field label="Which Spider network?">
                  {parsed.origin_candidates?.length ? (
                    <Select value={originId} onChange={(e) => setOriginId(e.target.value)} data-testid="voice-cmd-origin"><option value="">— select —</option>{parsed.origin_candidates.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</Select>
                  ) : <div className="si-label" style={{ color: "var(--si-warning)" }}>No Spider network matches “{p.origin_name}”.</div>}
                </Field>
              )}
              {intent === "add_note" && <Field label="Note text"><Textarea value={p.note || ""} onChange={(e) => setParsed({ ...parsed, parsed: { ...p, note: e.target.value } })} /></Field>}
              <div className="flex justify-between">
                <Btn variant="ghost" onClick={() => { stopAll(); onClose(); }}>Cancel</Btn>
                <Btn variant="primary" onClick={execute} disabled={busy} data-testid="voice-cmd-confirm"><Check size={15} /> {busy ? "Working…" : "Confirm"}</Btn>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
    )}
    </>
  );
}
